import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BookOpen,
  FolderTree,
  Search,
  ArrowRight,
  CheckCircle,
  Circle,
  Settings,
  Plus
} from 'lucide-react';
import { apiService } from '../services/api';
import { LearningCollection } from '../types';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const CollectionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [collections, setCollections] = useState<LearningCollection[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [assignedCollectionIds, setAssignedCollectionIds] = useState<string[]>([]);
  const [completedTutorialIds, setCompletedTutorialIds] = useState<Record<string, string[]>>({});

  const canManage = user?.roles?.includes('admin') || 
                   user?.roles?.includes('manager') || 
                   user?.roles?.includes('instructor');

  useEffect(() => {
    loadCollections();
    if (user?.id) {
      loadAssignedCollections();
    }
  }, [user?.id]);

  const loadCollections = async () => {
    try {
      setLoading(true);
      const response = await apiService.getLearningCollections();
      if (response.success && response.data) {
        setCollections(response.data);
        // Load progress for each collection
        if (user?.id) {
          loadProgressForCollections(response.data);
        }
      }
    } catch (error: any) {
      console.error('Error loading collections:', error);
      toast.error('Failed to load collections');
    } finally {
      setLoading(false);
    }
  };

  const loadAssignedCollections = async () => {
    try {
      const response = await apiService.getAssignedCollections();
      if (response.success && response.data) {
        setAssignedCollectionIds(response.data.map((c: LearningCollection) => c.id));
      }
    } catch (error) {
      console.error('Error loading assigned collections:', error);
    }
  };

  const loadProgressForCollections = async (collectionsToLoad: LearningCollection[]) => {
    if (!user?.id) return;

    const progressPromises = collectionsToLoad.map(async (collection) => {
      try {
        const response = await apiService.getCollectionProgress(collection.id);
        if (response.success && response.data) {
          return {
            collectionId: collection.id,
            completedIds: response.data.completedTutorialIds || []
          };
        }
      } catch (error) {
        console.error(`Error loading progress for collection ${collection.id}:`, error);
      }
      return null;
    });

    const results = await Promise.all(progressPromises);
    const progressMap: Record<string, string[]> = {};
    results.forEach((result) => {
      if (result) {
        progressMap[result.collectionId] = result.completedIds;
      }
    });
    setCompletedTutorialIds(progressMap);
  };

  // Filter collections based on search
  const filteredCollections = collections.filter(collection =>
    collection.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    collection.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading collections...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Learning Collections</h1>
              <p className="text-gray-600">
                Browse curated collections of tutorials and categories organized for your learning journey
              </p>
            </div>
            {canManage && (
              <button
                onClick={() => navigate('/admin/collections')}
                className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Settings className="w-4 h-4" />
                <span>Manage Collections</span>
              </button>
            )}
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search collections..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Collections Grid */}
        {filteredCollections.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCollections.map((collection) => {
              const isAssigned = assignedCollectionIds.includes(collection.id);
              const completedIds = completedTutorialIds[collection.id] || [];
              const totalTutorials = collection.totalTutorialCount || collection.tutorialCount || 0;
              
              // Calculate progress if user has progress data
              // Load full collection data to get all tutorials (including from categories)
              let progressPercentage = 0;
              let completedCount = 0;
              let uniqueTutorialsCount = 0;
              
              if (user && totalTutorials > 0) {
                // Try to get tutorials from collection data if available
                if (collection.tutorials || collection.categories) {
                  const allTutorials = [
                    ...(collection.tutorials || []),
                    ...(collection.categories || []).flatMap(cat => {
                      // Recursively get tutorials from subcategories
                      const getTutorialsFromCategory = (cat: any): any[] => {
                        const catTutorials = cat.tutorials || [];
                        const subcatTutorials = (cat.subcategories || []).flatMap((subcat: any) => 
                          getTutorialsFromCategory(subcat)
                        );
                        return [...catTutorials, ...subcatTutorials];
                      };
                      return getTutorialsFromCategory(cat);
                    })
                  ];
                  const uniqueTutorials = allTutorials.filter((t, index, self) =>
                    index === self.findIndex(t2 => t2.id === t.id)
                  );
                  uniqueTutorialsCount = uniqueTutorials.length;
                  completedCount = uniqueTutorials.filter(t => completedIds.includes(t.id)).length;
                  progressPercentage = uniqueTutorialsCount > 0 
                    ? (completedCount / uniqueTutorialsCount) * 100 
                    : 0;
                } else {
                  // Fallback: use completedIds count if we have progress data
                  completedCount = completedIds.length;
                  progressPercentage = totalTutorials > 0 
                    ? (completedCount / totalTutorials) * 100 
                    : 0;
                }
              }

              // Show progress if user has any progress, regardless of assignment
              const hasProgress = completedCount > 0 || progressPercentage > 0;

              return (
                <motion.div
                  key={collection.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  onClick={() => navigate(`/collections/${collection.id}`)}
                  className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start space-x-3 flex-1">
                      {collection.icon && (
                        <div className="text-3xl">{collection.icon}</div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          {collection.name}
                        </h3>
                        {collection.description && (
                          <p className="text-sm text-gray-600 line-clamp-2">
                            {collection.description}
                          </p>
                        )}
                      </div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-400 flex-shrink-0 ml-2" />
                  </div>

                  {/* Progress Bar - Show if user has any progress, even if not assigned */}
                  {user && hasProgress && totalTutorials > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Your Progress</span>
                        <span className="text-sm text-gray-600">
                          {completedCount} of {uniqueTutorialsCount || totalTutorials} ({Math.round(progressPercentage)}%)
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${progressPercentage}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Stats */}
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span className="flex items-center space-x-1">
                      <BookOpen className="w-3 h-3" />
                      <span>{totalTutorials} tutorial{totalTutorials !== 1 ? 's' : ''}</span>
                    </span>
                    {collection.categoryCount && collection.categoryCount > 0 && (
                      <span className="flex items-center space-x-1">
                        <FolderTree className="w-3 h-3" />
                        <span>{collection.categoryCount} categor{collection.categoryCount !== 1 ? 'ies' : 'y'}</span>
                      </span>
                    )}
                  </div>

                  {/* Assigned Badge */}
                  {isAssigned && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Assigned to you
                      </span>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchQuery ? 'No collections found' : 'No collections available'}
            </h3>
            <p className="text-gray-600">
              {searchQuery 
                ? 'Try adjusting your search terms'
                : 'Collections will appear here once they are created'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectionsPage;

